import type {
  Document,
  FileMapping,
  FileUploadMetadata,
  MappingAndErrors,
  Metadata,
  OperationResult,
  SeriesCell,
  TimeSeriesPersistResult,
  UploadTypeEnum,
} from 'venn-api';
import {
  correctData,
  parseDocument,
  persistTimeSeries,
  updateMapping,
  uploadDocument,
  uploadNavsParser,
  uploadPrivatesXLS,
} from 'venn-api';
import { useRecoilValue } from 'recoil';
import { proxySetIdState } from 'venn-state';
import type { ErrorViewModel } from './views/review/helpers';

type UploadFetcher = (file: File | Blob, uploadType: UploadTypeEnum, draft: boolean) => Promise<Document>;
type UpdateMappingFetcher = (fileId: number, mapping: FileMapping) => Promise<MappingAndErrors>;
type CorrectionFetcher = (fileId: number, corrections: ErrorViewModel[]) => Promise<MappingAndErrors>;
type PersistFetcher = (fileId: number, uploadType: UploadTypeEnum) => Promise<TimeSeriesPersistResult>;

async function apiWrapper<T, A extends unknown[]>(fetcher: (...args: A) => Promise<OperationResult<T>>, ...args: A) {
  try {
    return (await fetcher(...args)).content;
  } catch (error) {
    const { content } = (await error) as OperationResult<T>;
    throw content;
  }
}

export const fetchUploadFile: UploadFetcher = async (file, uploadType: UploadTypeEnum, draft: boolean) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('uploadType', uploadType);
  formData.append('draft', String(draft));
  return apiWrapper(uploadDocument, true, uploadType, formData);
};

export const fetchParsedFile = async (document: Document, proxySetId: string | undefined) =>
  apiWrapper(parseDocument, document.uuid, document.version, proxySetId);

export const fetchNewNavsUploadFile = async (file: File | Blob, uploadType: UploadTypeEnum) => {
  const formData = new FormData();
  formData.append('file', file);
  return apiWrapper(uploadNavsParser, uploadType, formData);
};

export const fetchPrivatesUploadFile = async (file: File | Blob) => {
  const formData = new FormData();
  formData.append('file', file);
  return apiWrapper(uploadPrivatesXLS, formData);
};

export const fetchUpdateMapping: UpdateMappingFetcher = async (fileId, body) =>
  apiWrapper(updateMapping, fileId, undefined, body);

export const fetchCorrectData: CorrectionFetcher = async (fileId, corrections) => {
  const body: SeriesCell[] = corrections.map(({ rowIndex, seriesId, value, date }) => ({
    index: rowIndex,
    seriesId,
    value,
    date,
  }));
  return apiWrapper(correctData, fileId, undefined, body);
};

export const fetchPersistSeries: PersistFetcher = async (fileId, uploadType) =>
  apiWrapper(persistTimeSeries, fileId, uploadType);

const typeIdCache: { [key: string]: string | number } = {};
export function getTypeId(
  metadata: FileUploadMetadata | undefined,
  namespace: keyof FileUploadMetadata,
  name: string,
): number {
  if (!metadata) return 0;
  const key = `${namespace}-${name}`;

  if (typeIdCache[key]) {
    return Number(typeIdCache[key]);
  }

  const foundId = metadata[namespace].find((a: Metadata<string> | Metadata<number>) => a.name === name);
  typeIdCache[key] = foundId!.id;
  return Number(foundId?.id);
}

// Hook-based functions that include analysisViewId
export function useDataUploaderFetchers() {
  const proxySetId = useRecoilValue(proxySetIdState);

  return {
    fetchUpdateMapping: async (fileId: number, body: FileMapping) =>
      apiWrapper(updateMapping, fileId, proxySetId, body),
    fetchCorrectData: async (fileId: number, corrections: ErrorViewModel[]) => {
      const body: SeriesCell[] = corrections.map(({ rowIndex, seriesId, value, date }) => ({
        index: rowIndex,
        seriesId,
        value,
        date,
      }));
      return apiWrapper(correctData, fileId, proxySetId, body);
    },
  };
}
